import { ChallengeService, TaskService } from '@duck/api-definitions'
import { TranslationKey } from '@/modules'
import {
  SparklesIcon,
  BoltIcon,
  ClockIcon,
  FireIcon
} from '@heroicons/react/24/outline'
import { ComponentType, SVGProps } from 'react'

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>

/**
 * Maps ChallengeService.TimeCategoryEnum to icons
 */
export const timeCategoryIconMap: Record<ChallengeService.TimeCategoryEnum, HeroIcon> = {
  [ChallengeService.TimeCategoryEnum.New]: SparklesIcon,
  [ChallengeService.TimeCategoryEnum.UpTo30]: BoltIcon,
  [ChallengeService.TimeCategoryEnum.UpTo60]: ClockIcon,
  [ChallengeService.TimeCategoryEnum._60OrMore]: FireIcon
}

/**
 * Maps ChallengeService.TimeCategoryEnum to translation keys
 */
export const timeCategoryDisplayMap: Record<ChallengeService.TimeCategoryEnum, TranslationKey> = {
  [ChallengeService.TimeCategoryEnum.New]: 'challenges.timeCategoryNew',
  [ChallengeService.TimeCategoryEnum.UpTo30]: 'challenges.timeCategoryUpTo30',
  [ChallengeService.TimeCategoryEnum.UpTo60]: 'challenges.timeCategoryUpTo60',
  [ChallengeService.TimeCategoryEnum._60OrMore]: 'challenges.timeCategory60OrMore'
}

/**
 * The order of time categories for display
 */
export const timeCategoryOrder: ChallengeService.TimeCategoryEnum[] = [
  ChallengeService.TimeCategoryEnum.New,
  ChallengeService.TimeCategoryEnum.UpTo30,
  ChallengeService.TimeCategoryEnum.UpTo60,
  ChallengeService.TimeCategoryEnum._60OrMore
]

/**
 * Time categories reuse the difficulty color ramp, mapped by ascending order:
 * New → Easy, Up to 30 → Intermediate, Up to 60 → Hard, Over 1h → Expert.
 * `dot` tints the filter facet bullet, `text` tints the group-header icon.
 */
export const timeCategoryHue: Record<ChallengeService.TimeCategoryEnum, { dot: string; text: string }> = {
  [ChallengeService.TimeCategoryEnum.New]: { dot: 'bg-emerald-400', text: 'text-emerald-400' },
  [ChallengeService.TimeCategoryEnum.UpTo30]: { dot: 'bg-sky-500', text: 'text-sky-500' },
  [ChallengeService.TimeCategoryEnum.UpTo60]: { dot: 'bg-amber-500', text: 'text-amber-500' },
  [ChallengeService.TimeCategoryEnum._60OrMore]: { dot: 'bg-rose-500', text: 'text-rose-500' },
}

/**
 * Maps TaskService.TaskDifficultyEnum to translation keys
 */
export const taskDifficultyDisplayMap: Record<TaskService.TaskDifficultyEnum, TranslationKey> = {
  [TaskService.TaskDifficultyEnum.Easy]: 'tasks.difficulty.easy',
  [TaskService.TaskDifficultyEnum.Intermediate]: 'tasks.difficulty.intermediate',
  [TaskService.TaskDifficultyEnum.Hard]: 'tasks.difficulty.hard',
  [TaskService.TaskDifficultyEnum.Expert]: 'tasks.difficulty.expert'
}

/**
 * Maps ChallengeService.ChallengeDifficultyEnum to translation keys
 */
export const challengeDifficultyDisplayMap: Record<ChallengeService.ChallengeDifficultyEnum, TranslationKey> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: 'tasks.difficulty.easy',
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'tasks.difficulty.intermediate',
  [ChallengeService.ChallengeDifficultyEnum.Hard]: 'tasks.difficulty.hard',
  [ChallengeService.ChallengeDifficultyEnum.Expert]: 'tasks.difficulty.expert'
}

/**
 * Shared identity color per difficulty — `dot` tints the filter facet bullet,
 * `text` tints the matching group-header icon so the two always agree.
 */
export const challengeDifficultyHue: Record<ChallengeService.ChallengeDifficultyEnum, { dot: string; text: string }> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: { dot: 'bg-emerald-400', text: 'text-emerald-400' },
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: { dot: 'bg-sky-500', text: 'text-sky-500' },
  [ChallengeService.ChallengeDifficultyEnum.Hard]: { dot: 'bg-amber-500', text: 'text-amber-500' },
  [ChallengeService.ChallengeDifficultyEnum.Expert]: { dot: 'bg-rose-500', text: 'text-rose-500' },
}

/**
 * Returns Tailwind CSS classes for TaskService difficulty badges
 */
export const getTaskDifficultyColor = (difficulty: TaskService.TaskDifficultyEnum): string => {
  const colorMap = {
    [TaskService.TaskDifficultyEnum.Easy]: 'bg-success/10 text-success border-success/20',
    [TaskService.TaskDifficultyEnum.Intermediate]: 'bg-info/10 text-muted-foreground border-info/20',
    [TaskService.TaskDifficultyEnum.Hard]: 'bg-warning/10 text-warning border-warning/20',
    [TaskService.TaskDifficultyEnum.Expert]: 'bg-error/10 text-error border-error/20'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

/**
 * Returns Tailwind CSS classes for ChallengeService difficulty badges.
 * Mirrors the `challengeDifficultyHue` ramp (emerald/sky/amber/rose) so the
 * card badge and the filter-rail facet dots share one color identity.
 */
export const getChallengeDifficultyColor = (difficulty: ChallengeService.ChallengeDifficultyEnum): string => {
  const colorMap = {
    [ChallengeService.ChallengeDifficultyEnum.Easy]: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30',
    [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'bg-sky-500/10 text-sky-500 border-sky-500/30',
    [ChallengeService.ChallengeDifficultyEnum.Hard]: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    [ChallengeService.ChallengeDifficultyEnum.Expert]: 'bg-rose-500/10 text-rose-500 border-rose-500/30'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

/**
 * Returns Tailwind CSS classes for ChallengeService difficulty badges with accent variant
 * Used in CompletedChallengesPage where Intermediate uses accent color instead of info
 */
export const getChallengeDifficultyColorAccentVariant = (difficulty: ChallengeService.ChallengeDifficultyEnum): string => {
  const colorMap = {
    [ChallengeService.ChallengeDifficultyEnum.Easy]: 'bg-success/10 text-success border-success/20',
    [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'bg-accent/10 text-accent border-accent/20',
    [ChallengeService.ChallengeDifficultyEnum.Hard]: 'bg-warning/10 text-warning border-warning/20',
    [ChallengeService.ChallengeDifficultyEnum.Expert]: 'bg-error/10 text-error border-error/20'
  }
  return colorMap[difficulty] || 'bg-muted/10 text-muted border-muted/20'
}

/**
 * Maps ChallengeService.ChallengeDifficultyEnum to numeric order for sorting
 */
export const challengeDifficultyOrder: Record<ChallengeService.ChallengeDifficultyEnum, number> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: 0,
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 1,
  [ChallengeService.ChallengeDifficultyEnum.Hard]: 2,
  [ChallengeService.ChallengeDifficultyEnum.Expert]: 3,
}

